import { AlertCircle } from "lucide-react";
import {
  describeRetryDelay,
  type PortalGetAccessRequest,
} from "@shared-core/domain/usePortalGetAccess";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { Input } from "../../../app/components/primitives/input";
import { Label } from "../../../app/components/primitives/label";

/**
 * The summary banner above whichever form made the request (api-validation.md: a field error
 * is shown next to its input AND summarised, so a screen-reader user hears that something is
 * wrong at submission). Only the two outcomes the person must act on show here; an accepted or
 * failed request is the page's "Check your email" screen, never a banner.
 */
function AccessRequestProblem({
  request,
}: {
  request: PortalGetAccessRequest;
}) {
  if (request.status !== "invalid_email" && request.status !== "throttled")
    return null;
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
    >
      <AlertCircle className="mt-0.5 size-4 shrink-0" />
      <span>
        {request.status === "invalid_email"
          ? "There is a problem with the email address you entered."
          : `Too many requests from this connection. Please try again ${describeRetryDelay(request.retryAfterSeconds)}.`}
      </span>
    </div>
  );
}

/**
 * "New here, or no password yet?": an email box that asks for a setup link. Shown only where the
 * sign-in options say Get access is on. The answer never depends on whether the address is
 * recognised, so this card words nothing about the outcome: the page shows the backend's message.
 */
export function PortalGetAccessCard({
  email,
  onEmailChange,
  onSubmit,
  request,
  isSending,
  isBusy,
}: {
  email: string;
  onEmailChange: (value: string) => void;
  onSubmit: () => void;
  /** This card's own request; idle when the other form made the last one. */
  request: PortalGetAccessRequest;
  /** This card's own request is out: the button says so. */
  isSending: boolean;
  /** Either form's request is out: no second one may start. */
  isBusy: boolean;
}) {
  const fieldError =
    request.status === "invalid_email" ? request.message : null;
  return (
    <Card className="p-5">
      <form
        className="space-y-3"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <div>
          <p className="text-sm font-medium">New here, or no password yet?</p>
          <p className="text-xs text-muted-foreground">
            Enter the email address you use with Pollards and we&apos;ll email
            you a link to set up your access. You&apos;ll set a password with a
            one-time code.
          </p>
        </div>
        <AccessRequestProblem request={request} />
        <div className="space-y-1.5">
          <Label htmlFor="portal-access-email">Email address</Label>
          <Input
            id="portal-access-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@yourcompany.co.uk"
            aria-invalid={fieldError ? true : undefined}
            aria-describedby={
              fieldError ? "portal-access-email-error" : undefined
            }
          />
          {fieldError && (
            <p
              id="portal-access-email-error"
              className="text-sm text-destructive"
            >
              {fieldError}
            </p>
          )}
        </div>
        <Button
          type="submit"
          variant="outline"
          className="w-full"
          disabled={isBusy}
        >
          {isSending ? "Sending…" : "Get access"}
        </Button>
      </form>
    </Card>
  );
}
